import React, { ReactNode, useState } from "react";
import { Flex, Grid, Input, Text } from "@chakra-ui/react";
import { Formik } from "formik";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import CenteredSpinner from "@/shared/components/centered-spinner";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetBuyerBankInfo from "@/shared/hooks/bank-info/useGetBuyerBankInfo";
import useUpdatePathPickOrder from "@/features/pathpick/features/order-history/hooks/mutation/useUpdatePathPickOrder";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrderRefundAccountPreview } from "@/features/pathpick/features/order-history/features/detail/types";
import { PathPickOrder } from "@/features/pathpick/features/order-history/types/response";

interface RefundAccountFormValues {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  // 저장 시 바이어 기본 환불 계좌로도 반영할지 — 편집 모드의 체크박스.
  saveAsDefault: boolean;
}

// 서버가 세 값을 모두 필수(1~50자)로 받으므로 하나라도 비면 저장할 수 없다.
const hasRequiredValues = (values: RefundAccountFormValues) =>
  Boolean(
    values.bankName.trim() &&
      values.accountNumber.trim() &&
      values.accountHolder.trim()
  );

// 계좌 입력값 최대 길이 — 서버 스키마(1~50자)와 맞춘다.
const MAX_LENGTH = 50;

const INPUT_STYLE_PROPS = {
  fontSize: "13px",
  border: "0.5px solid #D4D4D1",
  borderRadius: "8px",
  background: "#FCFCFB",
  _placeholder: { color: "#8F8F8C" },
};

interface Props {
  // 실사용 — 이 주문의 환불 계좌 스냅샷을 보여주고 수정한다.
  order?: PathPickOrder;
  // 있으면 시작 가이드의 화면 미리보기 — 조회/저장 훅을 타지 않고 이 값만 보여준다.
  preview?: PathPickOrderRefundAccountPreview;
}

// 환불 계좌 정보 — 주문 시점 스냅샷.
// 평소에는 읽기 전용으로 보여주고, "계좌 수정"을 눌러야 편집 모드가 된다(입금자명과 같은 관습).
// 저장은 이 주문의 스냅샷만 갱신하고, 체크박스를 켠 경우에만 기본 환불 계좌에도 반영한다.
//
// 미리보기와 실사용을 컴포넌트로 갈라 둔 이유는 훅이다 — 조회/저장 훅은 조건부로 부를 수 없어서,
// 미리보기가 훅을 아예 타지 않으려면 컴포넌트 경계로 나누는 수밖에 없다.
function PathPickOrderDetailRefundAccount({ order, preview }: Props) {
  return (
    <RefundAccountShell>
      {preview || !order ? (
        <RefundAccountReadOnly
          account={
            preview ?? { bankName: "", accountNumber: "", accountHolder: "" }
          }
        />
      ) : (
        <RefundAccountForm order={order} />
      )}
    </RefundAccountShell>
  );
}

// 제목·설명은 두 갈래가 같으므로 셸에 둔다.
function RefundAccountShell({ children }: { children: ReactNode }) {
  const { t } = useI18nHelper();

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      <Flex flexDirection={"column"} gap={"2px"}>
        <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
          {t(`${ORDER_DETAIL_KEY}refund_account_title`)}
        </Text>
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          {t(`${ORDER_DETAIL_KEY}refund_account_desc`)}
        </Text>
      </Flex>
      {children}
    </Flex>
  );
}

function RefundAccountForm({ order }: { order: PathPickOrder }) {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });
  const [isEditing, setIsEditing] = useState(false);
  // 스냅샷이 없는 과거 주문의 표시·프리필용 — 바이어 기본 환불 계좌.
  const { buyerBankInfo, isPending } = useGetBuyerBankInfo();
  const { mutate: updateOrder, isPending: isSaving } = useUpdatePathPickOrder(
    order.id
  );

  // 환불이 끝난 주문은 계좌를 바꿔도 의미가 없다 — 서버도 400으로 막는다.
  const isRefundCompleted = Boolean(order.refund?.completedAt);
  const snapshot = order.refundAccount;
  const displayed = {
    bankName: snapshot?.bankName ?? buyerBankInfo?.bankName ?? "",
    accountNumber: snapshot?.accountNumber ?? buyerBankInfo?.accountNumber ?? "",
    accountHolder: snapshot?.accountHolder ?? buyerBankInfo?.accountHolder ?? "",
  };

  // 저장된 계좌가 있으면 인풋에 채워 보여주므로, 조회 전에는 빈 인풋 대신 스피너를 둔다.
  if (isPending) {
    return <CenteredSpinner padding={"24px 0"} />;
  }

  if (!isEditing) {
    return (
      <Flex flexDirection={"column"} gap={"12px"}>
        <RefundAccountReadOnly account={displayed} />
        {!isRefundCompleted && (
          <AppButton
            variant={"white"}
            onClick={() => setIsEditing(true)}
            chakraProps={{ alignSelf: "flex-start" }}
          >
            <Text textStyle={"body-sm-bold"}>
              {t(`${ORDER_DETAIL_KEY}refund_account_edit`)}
            </Text>
          </AppButton>
        )}
      </Flex>
    );
  }

  return (
    <Formik<RefundAccountFormValues>
      enableReinitialize
      initialValues={{ ...displayed, saveAsDefault: false }}
      onSubmit={(values) =>
        updateOrder(
          {
            refundBankName: values.bankName.trim(),
            refundAccountNumber: values.accountNumber.trim(),
            refundAccountHolder: values.accountHolder.trim(),
            saveRefundAccountAsDefault: values.saveAsDefault,
          },
          {
            onSuccess: () => {
              setIsEditing(false);
              info(t(`${ORDER_DETAIL_KEY}refund_account_save_success`));
            },
          }
        )
      }
    >
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <Flex flexDirection={"column"} gap={"12px"}>
          <RefundAccountFieldGrid>
            <RefundAccountField
              label={t(`${ORDER_DETAIL_KEY}refund_account_bank`)}
            >
              <Input
                name={"bankName"}
                value={values.bankName}
                onChange={handleChange}
                maxLength={MAX_LENGTH}
                {...INPUT_STYLE_PROPS}
              />
            </RefundAccountField>

            <RefundAccountField
              label={t(`${ORDER_DETAIL_KEY}refund_account_number`)}
            >
              <Input
                name={"accountNumber"}
                value={values.accountNumber}
                onChange={handleChange}
                maxLength={MAX_LENGTH}
                {...INPUT_STYLE_PROPS}
              />
            </RefundAccountField>

            <RefundAccountField
              label={t(`${ORDER_DETAIL_KEY}refund_account_holder`)}
            >
              <Input
                name={"accountHolder"}
                value={values.accountHolder}
                onChange={handleChange}
                maxLength={MAX_LENGTH}
                {...INPUT_STYLE_PROPS}
              />
            </RefundAccountField>
          </RefundAccountFieldGrid>

          {/* 이 주문의 계좌만 바꾸는 것이 기본 — 내 정보(기본 환불 계좌) 반영은 선택. */}
          <Flex gap={"6px"} alignItems={"center"}>
            <AppCheckBox
              isChecked={values.saveAsDefault}
              size={"small"}
              onChange={(checked) => setFieldValue("saveAsDefault", checked)}
            />
            <Text
              textStyle={"caption-regular"}
              color={"#444440"}
              cursor={"pointer"}
              onClick={() => setFieldValue("saveAsDefault", !values.saveAsDefault)}
            >
              {t(`${ORDER_DETAIL_KEY}refund_account_save_default`)}
            </Text>
          </Flex>

          <Flex gap={"8px"}>
            <AppButton
              variant={"primary"}
              isDisabled={!hasRequiredValues(values)}
              isLoading={isSaving}
              onClick={() => handleSubmit()}
            >
              <Text textStyle={"body-sm-bold"}>
                {t(`${ORDER_DETAIL_KEY}refund_account_save`)}
              </Text>
            </AppButton>
            <AppButton
              variant={"white"}
              isDisabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              <Text textStyle={"body-sm-bold"}>
                {t(`${ORDER_DETAIL_KEY}refund_account_cancel`)}
              </Text>
            </AppButton>
          </Flex>
        </Flex>
      )}
    </Formik>
  );
}

// 읽기 전용 표시 — 비활성 인풋 3칸. 미리보기(시작 가이드)와 실사용 읽기 모드가 함께 쓴다.
function RefundAccountReadOnly({
  account,
}: {
  account: PathPickOrderRefundAccountPreview;
}) {
  const { t } = useI18nHelper();

  return (
    <RefundAccountFieldGrid>
      <RefundAccountField label={t(`${ORDER_DETAIL_KEY}refund_account_bank`)}>
        <Input value={account.bankName} isDisabled {...INPUT_STYLE_PROPS} />
      </RefundAccountField>

      <RefundAccountField label={t(`${ORDER_DETAIL_KEY}refund_account_number`)}>
        <Input
          value={account.accountNumber}
          isDisabled
          {...INPUT_STYLE_PROPS}
        />
      </RefundAccountField>

      <RefundAccountField label={t(`${ORDER_DETAIL_KEY}refund_account_holder`)}>
        <Input
          value={account.accountHolder}
          isDisabled
          {...INPUT_STYLE_PROPS}
        />
      </RefundAccountField>
    </RefundAccountFieldGrid>
  );
}

function RefundAccountFieldGrid({ children }: { children: ReactNode }) {
  return (
    <Grid
      templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
      gap={"16px"}
      alignItems={"end"}
    >
      {children}
    </Grid>
  );
}

// 라벨 스타일은 같은 카드의 PathPickOrderDetailInfoField와 맞춘다.
function RefundAccountField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Flex flexDirection={"column"} gap={"4px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      {children}
    </Flex>
  );
}

export default PathPickOrderDetailRefundAccount;
